import React, { useState, useEffect } from "react";
import LoginHeader from "./LoginHeader";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUser, checkEmail } from "../../store/authSlice";
import CreatePassword from "../../Registration/PasswordPage";
import { updateUserPassword } from "../../store/authSlice";
import {
  setSupplierId,
  setAccountType,
} from "../../store/supplierFormDataSlice";
import {
  setBasicInfo,
  setBusinessLocation,
  setServicesAndCapabilities,
  setComplianceInfo,
  setDiversityInfo,
  setEndorstmentInfo,
  setMembershipsCertifications,
} from "../../store/supplierFormDataSlice";
import {
  saveCompanyInfoToStore,
  saveProcurementInfoToStore,
  saveDiversityInfoToStore,
} from "../../store/buyerFormDataSlice";
import { savePersonalInfoToStore } from "../../store/personalInfoSlice";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isPasswordSet, setIsPasswordSet] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [err, setError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordUpdateError, setPasswordUpdateError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [userId, setuserId] = useState("");
  const [passwordValidationClicked, setPasswordValidationClicked] =
    useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    newPassword: false,
    confirmPassword: false,
  });
  const profile = useSelector((state) => state.auth.profile);
  const personalDetails = useSelector((state) => state.auth.personalDetails);
  const accountType = useSelector((state) => state.auth.role);

  useEffect(() => {
    if (profile && accountType === "SUPPLIER") {
      const {
        company,
        address,
        identifiers,
        industry,
        endorsements,
        associations,
        diversity,
      } = profile;

      dispatch(setSupplierId(profile._id));
      dispatch(setAccountType(profile.accountType));

      const basicInfo = {
        name: company?.name,
        legalName: company?.legalName,
        dba: company?.dba,
        description: company?.description,
        businessPhone: company?.businessPhone,
        website: company?.website,
        foundedYear: company?.foundedYear,
      };
      dispatch(setBasicInfo(basicInfo));

      const businessLocation = {
        country: address?.type?.[address?.type.length - 1]?.country || "USA",
        state: address?.type?.[address?.type.length - 1]?.state || "",
        city: address?.type?.[address?.type.length - 1]?.city || "",
        address_line_01:
          address?.type?.[address?.type.length - 1]?.address_line_01 || "",
        address_line_02:
          address?.type?.[address?.type.length - 1]?.address_line_02 || "",
        postal_code:
          address?.type?.[address?.type.length - 1]?.postal_code || "",
      };

      dispatch(setBusinessLocation(businessLocation));

      const identifierDetails = identifiers?.identifierDetails || [];
      const primaryNAICS =
        identifierDetails.find(
          (item) =>
            item.type === "NAICS" && item.description === "Primary NAICS"
        ) || {};
      const additionalNAICS = identifierDetails
        .filter(
          (item) =>
            item.type === "NAICS" && item.description === "Additional NAICS"
        )
        .map(({ code, name, description }) => ({ code, name, description }));

      const servicesAndCapabilities = {
        industry: industry?.name || "",
        primaryNAICS,
        additionalNAICS,
        servicesOffered: [],
      };

      dispatch(setServicesAndCapabilities(servicesAndCapabilities));

      const complianceFile =
        profile?.complianceFile ||
        new File(["dummy content"], "example.pdf", { type: "application/pdf" });
      const complianceInfo = {
        id: profile._id,
        ein: {
          name: "Tax ID / EIN",
          code: identifiers?.identifierDetails[0]?.code,
        },
        duns:
          identifiers?.identifierDetails.find(
            (item) => item.name === "DUNS Number"
          ) || null,
        cage:
          identifiers?.identifierDetails.find(
            (item) => item.name === "CAGE Code"
          ) || null,
        entityType: company?.entityType,
      };
      dispatch(setComplianceInfo({ file: complianceFile, complianceInfo }));

      const certificationTypes =
        diversity?.certificationType?.map((data) => ({
          certName: data.classification || "",
          certId: data.certificationNumber || "",
          agency: data.agency || "",
          file: data.file || null,
        })) || [];

      const agencyDetails =
        diversity?.agency?.details?.map((data) => ({
          agencyName: data.name || "",
          isCertified: data.isCertified || false,
          agencyDescription: data.description || "",
        })) || [];

      dispatch(setDiversityInfo({ certificationTypes, agencyDetails }));

      const endorsementsList =
        endorsements?.testimonials?.map((item) => ({
          type: item.reference ? "Professional References" : "Client",
          name: item.endorsementInfo?.name || "",
          feedback: item.endorsementInfo?.feedback || "",
          contact: item.endorsementInfo?.contact || "",
          file: item.file || null,
        })) || [];
      dispatch(setEndorstmentInfo(endorsementsList));

      const membershipsCertifications = {
        memberships: associations?.memberships || [],
        certifications: associations?.certifications || [],
      };
      dispatch(setMembershipsCertifications(membershipsCertifications));
    }

    if (profile && accountType === "BUYER") {
      const { company, procurementDetails, supplierDiversityObjectives } =
        profile;

      const companyInfo = {
        companyName: company?.name,
        organizationType: company?.organizationType,
        otherOrganizationType: company?.otherOrganizationType || "",
        companySize: company?.companySize,
        industry: company?.industry,
      };
      dispatch(saveCompanyInfoToStore(companyInfo));

      const procurementInfo = {
        jobTitle: procurementDetails?.jobTitle,
        procurementExperience: procurementDetails?.procurementExperience,
        specializations: procurementDetails?.specializations,
      };

      dispatch(saveProcurementInfoToStore(procurementInfo));

      const supplierDiversityInfo = {
        objectives: supplierDiversityObjectives?.objectives,
      };

      dispatch(saveDiversityInfoToStore(supplierDiversityInfo));
    }
    if (personalDetails) {
      const userData = {
        profilePicture: personalDetails.profilePictureUrl,
        firstName: personalDetails.firstName,
        lastName: personalDetails.lastName,
        country: personalDetails.country,
        stateProvince: personalDetails.state,
        city: personalDetails.city,
        personalDetails: personalDetails.personalDetails,
        phone: personalDetails.phone,
      };

      dispatch(savePersonalInfoToStore(userData));
    }
  }, [profile, personalDetails, dispatch]);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);

    if (!isChecked) {
      localStorage.removeItem("rememberedEmail");
    }
  };

  useEffect(() => {
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
    }
  }, [email, rememberMe]);

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);
  const handleClick = (event) => {
    event.preventDefault();

    if (newPassword && confirmPassword) {
      handlePasswordUpdate(event);
    } else {
      handleSubmit(event);
    }
  };

  const handleEmailBlur = async () => {
    if (!validateEmail(email)) {
      setEmailError("Enter a valid email.");
      return;
    }
    try {
      const response = await dispatch(checkEmail({ email })).unwrap();

      if (!response.emailExists) {
        return;
      }

      setEmailChecked(response.emailExists);
      setIsPasswordSet(response.hasPassword);
      setuserId(response.id);
    } catch (error) {
      console.error("Error checking email:", error.message || error);
      setEmailError("Error checking email.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setEmailError("");
    setPasswordError("");

    if (!email) {
      setEmailError("Email is required.");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Enter a valid email.");
      return;
    }
    if (!password) {
      setPasswordError("Password is required.");
      return;
    }

    try {
      const result = await dispatch(loginUser({ email, password })).unwrap();

      if (result?.redirect) {
        navigate(result.redirect);
        return;
      }

      if (!result?.token) {
        setError("Invalid user credentials. Please try again.");
        return;
      }

      rememberMe
        ? localStorage.setItem("rememberedEmail", email)
        : localStorage.removeItem("rememberedEmail");

      const { profile, hasProfileDetails, accountType, hasNameDetails } =
        result;

      if (profile?.misc?.isRegistrationComplete) {
        if (accountType === "BUYER") {
          // navigate("/dashboard");
          navigate("/welcome-page")
        } else {
          navigate(
            accountType === "SUPPLIER"
              ? // ? "/supplier-dashboard"
                "/welcome-page"
              : "/login"
          );
        }
        return;
      }
      if (
        (!profile.company || profile.company?.name === "") &&
        hasNameDetails &&
        hasProfileDetails
      ) {
        if (accountType === "BUYER") {
          navigate("/buyer-profile-registration/company-details");
        } else if (accountType === "SUPPLIER") {
          navigate("/supplier-profile-registration/basic-information");
        }
        return;
      }

      if (
        profile &&
        !profile?.misc?.isRegistrationComplete &&
        hasProfileDetails
      ) {
        if (accountType === "SUPPLIER") {
          const steps = [
            { id: 1, slug: "basic-information" },
            { id: 2, slug: "business-location" },
            { id: 3, slug: "registration-compliance" },
            { id: 4, slug: "services-capabilities" },
            { id: 5, slug: "diversity-verification" },
            { id: 6, slug: "testimonials-references" },
            { id: 7, slug: "membership-certifications" },
            { id: 8, slug: "status-verification" },
          ];

          const currentStepSlug = steps.find(
            (step) => step.id === profile.misc.currentRegistrationStep + 1
          )?.slug;

          if (currentStepSlug) {
            navigate(`/supplier-profile-registration/${currentStepSlug}`);
            return;
          }
        }

        if (accountType === "BUYER") {
          const buyerSteps = [
            { id: 1, slug: "company-details" },
            { id: 2, slug: "procurement-role" },
            { id: 3, slug: "supplier-diversity" },
            { id: 4, slug: "review" },
            { id: 5, slug: "registration-confirmation" },
          ];

          const currentStepSlug = buyerSteps.find(
            (step) => step.id === profile.misc.currentRegistrationStep + 1
          )?.slug;

          if (currentStepSlug) {
            navigate(`/buyer-profile-registration/${currentStepSlug}`);
            return;
          }
        }
      }

      setError("Invalid user credentials. Please try again.");
    } catch (error) {
      if (error?.redirect) {
        navigate(error.redirect);
      } else {
        setError(
          error?.message || "Invalid user credentials. Please try again."
        );
        setPassword("");
      }
    }
  };

  const handlePasswordUpdate = async () => {
    setPasswordValidationClicked(true);

    let validationErrors = [];
    let validLength = newPassword.length >= 8;
    let validUppercase = /[A-Z]/.test(newPassword);
    let validNumber = /\d/.test(newPassword);
    let validCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    let validMatch = newPassword === confirmPassword;

    if (!validLength)
      validationErrors.push("Password must be at least 8 characters long.");
    if (!validUppercase)
      validationErrors.push(
        "Password must contain at least one uppercase letter."
      );
    if (!validNumber)
      validationErrors.push("Password must contain at least one number.");
    if (!validCharacter)
      validationErrors.push(
        "Password must contain at least one special character."
      );
    if (!validMatch) validationErrors.push("Passwords do not match.");

    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      setPasswordError(validationErrors.join(" "));
      return;
    }
    try {
      const result = await dispatch(
        updateUserPassword({ userId, password: newPassword })
      ).unwrap();

      if (result.success) {
        navigate("/account-confirmation", { state: { userId: result.id } });
      } else {
        setPasswordUpdateError("Password update failed.");
      }
    } catch (error) {
      setPasswordUpdateError(error.message || "Failed to update password.");
    }
  };

  return (
    <div className="w-full h-screen">
      <LoginHeader
        loginPage={true}
        emailChecked={emailChecked}
        isPasswordSet={isPasswordSet}
      />
      <div
        className="flex w-full flex-col justify-start items-center bg-white py-8 px-4 sm:px-6 md:px-8 pt-[155px]"
        role="main"
      >
        <h2
          className="text-[#1D2739] text-center font-semibold font-['Hanken Grotesk'] text-[24px] leading-normal tracking-[0.48px] mb-8 sm:mb-16 max-w-[350px] sm:max-w-[500px] mx-auto"
          id="login-heading"
        >
          {emailChecked && !isPasswordSet ? (
            <>
              <span className="block">Set Up Your Password</span>
              <p className="text-[#7E7E7E] font-['Hanken Grotesk'] text-[16px] font-medium leading-[23.124px] mt-4">
                It looks like you haven’t set a password yet.
              </p>
              <p className="text-[#7E7E7E] font-['Hanken Grotesk'] text-[16px] font-medium leading-[23.124px]">
                Create one now to complete your account setup.
              </p>
            </>
          ) : (
            "Login"
          )}
        </h2>

        <main
          className="w-full flex flex-col justify-center items-center gap-16 sm:max-w-md md:max-w-lg lg:max-w-xl"
          aria-labelledby="login-heading"
        >
          <form
            className="flex flex-col justify-center w-full"
            onSubmit={handleSubmit}
          >
            <fieldset
              className="w-full h-auto flex flex-col justify-center items-start gap-2 mb-10"
              aria-labelledby="email-label"
            >
              <legend className="sr-only">Login credentials</legend>
              <label
                htmlFor="email"
                id="email-label"
                className="text-[#1d2639] text-base font-normal font-['Lato']"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                className={`w-full h-12 p-3 bg-[#f9f9f9] rounded-lg border-2 ${
                  emailError ? "border-solid border-1 border-red-500" : ""
                }`}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                  if (err) setError("");
                }}
                onBlur={handleEmailBlur}
                disabled={emailChecked && !isPasswordSet}
              />
              {emailError && (
                <div
                  id="email-description"
                  className="text-red-500 text-sm mt-2"
                >
                  {emailError}
                </div>
              )}
            </fieldset>

            {emailChecked && !isPasswordSet ? (
              <motion.div
                key="createPassword"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <CreatePassword
                  newPassword={newPassword}
                  setNewPassword={setNewPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  passwordVisibility={passwordVisibility}
                  togglePasswordVisibility={togglePasswordVisibility}
                  passwordValidationClicked={passwordValidationClicked}
                  passwordError={passwordError}
                  setPasswordError={setPasswordError}
                  validationErrors={validationErrors}
                />
              </motion.div>
            ) : (
              <>
                <fieldset className="w-full h-auto flex flex-col justify-center items-start gap-2">
                  <legend className="sr-only">Password Input</legend>
                  <label
                    htmlFor="password"
                    className="text-[#1d2639] text-base font-normal font-['Lato']"
                  >
                    Password
                  </label>
                  <div className="relative w-full">
                    <input
                      type={passwordVisibility.password ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      className={`w-full h-12 p-3 pr-10 bg-[#f8f8f8] rounded-lg border-2 ${
                        passwordError
                          ? "border-solid border-2 border-red-500"
                          : ""
                      }`}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError("");
                        if (err) setError("");
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("password")}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                      >
                        <path
                          d="M7.99984 6.5C7.4694 6.5 6.9607 6.71071 6.58562 7.08579C6.21055 7.46086 5.99984 7.96957 5.99984 8.5C5.99984 9.03043 6.21055 9.53914 6.58562 9.91421C6.9607 10.2893 7.4694 10.5 7.99984 10.5C8.53027 10.5 9.03898 10.2893 9.41405 9.91421C9.78912 9.53914 9.99984 9.03043 9.99984 8.5C9.99984 7.96957 9.78912 7.46086 9.41405 7.08579C9.03898 6.71071 8.53027 6.5 7.99984 6.5ZM7.99984 11.8333C7.11578 11.8333 6.26794 11.4821 5.64281 10.857C5.01769 10.2319 4.6665 9.38406 4.6665 8.5C4.6665 7.61595 5.01769 6.7681 5.64281 6.14298C6.26794 5.51786 7.11578 5.16667 7.99984 5.16667C8.88389 5.16667 9.73174 5.51786 10.3569 6.14298C10.982 6.7681 11.3332 7.61595 11.3332 8.5C11.3332 9.38406 10.982 10.2319 10.3569 10.857C9.73174 11.4821 8.88389 11.8333 7.99984 11.8333ZM7.99984 3.5C4.6665 3.5 1.81984 5.57333 0.666504 8.5C1.81984 11.4267 4.6665 13.5 7.99984 13.5C11.3332 13.5 14.1798 11.4267 15.3332 8.5C14.1798 5.57333 11.3332 3.5 7.99984 3.5Z"
                          fill="currentColor"
                        ></path>
                      </svg>
                    </button>
                  </div>
                  {passwordError && (
                    <div
                      id="password-description"
                      className="text-red-500 text-sm mt-2"
                    >
                      {passwordError}
                    </div>
                  )}

                  {err && (
                    <div
                      id="email-description"
                      className="text-red-500 text-sm mt-2"
                    >
                      {err}
                    </div>
                  )}
                </fieldset>
                <div className="flex justify-between items-center w-full mb-10 mt-2">
                  <label
                    htmlFor="rememberMe"
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      id="rememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                      aria-describedby="rememberMeDescription"
                    />
                    <span id="rememberMeDescription" className="text-[#7E7E7E]">
                      Remember me
                    </span>
                  </label>

                  <a
                    href="/forgot-password"
                    className="text-[#7E7E7E] hover:underline focus:outline-none focus:ring-2"
                  >
                    Forgot password?
                  </a>
                </div>
              </>
            )}

            <div className="flex flex-col gap-4 mt-10">
              <button
                type="button"
                className={`flex justify-center cursor-pointer items-center self-center w-full sm:w-[432px] h-14 p-5
            ${
              (email && !emailError && password) ||
              (!isPasswordSet && newPassword && confirmPassword)
                ? "bg-[#eaf524] text-black"
                : "bg-[#c4c4c4] text-white"
            }
                        rounded-[13px] text-center text-base font-medium font-['Hanken Grotesk']`}
                aria-label="Continue to login"
                onClick={handleClick}
              >
                Continue
              </button>

              <button
                type="button"
                className="flex justify-center cursor-pointer items-center self-center w-full sm:w-[432px] h-14 p-5 rounded-[13px] border border-[#c4c4c4] text-center text-[#7e7e7e] text-base font-medium font-['Hanken Grotesk']"
                aria-label="Sign in with SSO"
              >
                Sign-in with SSO
              </button> 
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default LoginPage;
